// assets/js/utils/phone-resolve.js
/**
 * Turn a phone number as a host typed it into the number we will dial.
 *
 * ── Why this exists ──────────────────────────────────────────────────────
 * Until 2026-10-06 a number without a country code was read as if its first
 * digits WERE a country code: the browser put a "+" in front of whatever was
 * typed, and the server sent anything starting with 0 to Israel. Measured over
 * all 18,033 stored guest numbers that day:
 *   - a Guadalajara list typed as "33 1234 5678" went to +33 (France): 14
 *     guests, almost all failed "not on WhatsApp";
 *   - Israeli "52…" numbers went to +52 (Mexico), a US "818…" to +81 (Japan),
 *     a "407…" to +40 (Romania);
 *   - French "06…" guests went to +972 6… and failed;
 *   - a Nigerian "070…" number was dropped without a trace.
 *
 * The standard answer, and what this does: parse with libphonenumber in a
 * DEFAULT COUNTRY the host can see and change (the wizard's "Country for
 * numbers without a country code"), store E.164, and send exactly what was
 * stored. A number is only ever rewritten when libphonenumber says the result
 * is a real number; anything else is left as typed and flagged.
 *
 * Two orderings were measured, not reasoned:
 *   - 11+ bare digits that are a valid number with a "+" in front are read as
 *     international BEFORE the default country. "151…" (11 digits, a US number
 *     typed without +) on an Israeli list parses as a valid-looking Israeli
 *     number too, and the US reading is the one that delivered.
 *   - 10 digits and under go to the default country first: "609…" on a US
 *     list is New Jersey, not +60 Malaysia; "33…" on a Mexican list is
 *     Guadalajara, not France.
 *
 * Two shapes libphonenumber calls invalid DO deliver through Meta, so they are
 * TOLERATED (kept as typed, no warning): Mexico's legacy +52 1 + 10 digits
 * (8 of 14 delivered, the rest were genuinely not on WhatsApp) and Brazilian
 * mobiles written without the leading 9 (all 4 delivered).
 *
 * Dual-environment like nanp-region.js: the browser passes nothing and the
 * global `libphonenumber` (assets/js/vendor/libphonenumber-max-1.13.14.js) is
 * used; the server requires this file and gets libphonenumber-js/max.
 */

let _lib = null;
function lib() {
  if (_lib) return _lib;
  if (typeof window !== 'undefined' && window.libphonenumber) _lib = window.libphonenumber;
  else if (typeof require === 'function') {
    try { _lib = require('libphonenumber-js/max'); } catch (e) { _lib = null; }
  }
  return _lib;
}

function parse(text, region) {
  const L = lib();
  if (!L) return null;
  try { return L.parsePhoneNumberFromString(text, region || undefined) || null; } catch (e) { return null; }
}

function valid(p) {
  return p && typeof p.isValid === 'function' && p.isValid() ? p : null;
}

/** Shapes Meta delivers although libphonenumber calls them invalid. Digits after "+". */
function isTolerated(digits) {
  // Mexico, legacy mobile prefix: 52 1 + 10 digits.
  if (/^521\d{10}$/.test(digits)) return true;
  // Brazil, mobile without the leading 9: 55 + 2-digit area + 8 digits starting 6-9.
  if (/^55[1-9]\d[6-9]\d{7}$/.test(digits)) return true;
  return false;
}

/**
 * The tolerated shapes are WhatsApp-only: carriers reject them. A Brazilian
 * "51 9900-4782" texted as typed hard-bounced at Brevo ("invalid MSISDN",
 * Vera Fest 2026-10-21) — every Brazilian mobile has had the 9 since 2016.
 * Mexico dropped the mobile "1" in 2019. Digits in, digits out (no "+").
 */
function smsDialable(digits) {
  const d = String(digits || '');
  let m = /^55([1-9]\d)([6-9]\d{7})$/.exec(d);
  if (m) return '55' + m[1] + '9' + m[2];
  m = /^521(\d{10})$/.exec(d);
  if (m) return '52' + m[1];
  return d;
}

const SCI = /^\+?(\d+)(?:[.,](\d+))?[eE]\+?(\d+)$/;

/** Expand an exact scientific-notation integer ("9.71501231234E+11"); lost:true when digits were rounded away. */
function expandScientific(raw) {
  const m = SCI.exec(String(raw).trim().replace(/\s+/g, ''));
  if (!m) return { isSci: false };
  const intPart = m[1];
  const frac = m[2] || '';
  const exp = parseInt(m[3], 10);
  if (frac.length > exp) return { isSci: true, lost: true };
  const value = (intPart + frac + '0'.repeat(exp - frac.length)).replace(/^0+/, '');
  const kept = (intPart + frac).replace(/^0+/, '');
  if (!value || kept.length < value.length) return { isSci: true, lost: true };
  return { isSci: true, digits: value };
}

/**
 * @param {string} raw     what the host typed / the file held
 * @param {object} [opts]
 * @param {string} [opts.region]  ISO country for numbers without a country code
 * @returns {{
 *   phone: string,      // what to store and dial: E.164 when we are sure, else the input (see status)
 *   original: string,   // trimmed input
 *   changed: boolean,
 *   status: 'empty'|'valid'|'assumed'|'tolerated'|'invalid'|'needs_country'|'precision',
 *   country: string|null,   // ISO country of `phone` when known
 *   via: 'international'|'national'|null,
 *   type: libphonenumber getType() ('MOBILE', 'FIXED_LINE', …) when known
 * }}
 *   valid         — libphonenumber says it is a real number; `phone` is E.164
 *   tolerated     — a shape Meta delivers; `phone` is "+digits" as typed
 *   invalid       — has a country code but cannot be a real number; kept as "+digits"
 *   assumed       — no country code; read as "+digits", which is a real number, but nobody said so
 *   needs_country — no country code and no way to place it; a trunk-0 number is kept as typed, anything else as "+digits"
 *   precision     — a spreadsheet rounded digits away ("9.71586E+11"); kept as typed
 */
function resolvePhone(raw, opts = {}) {
  const region = opts.region ? String(opts.region).toUpperCase() : null;
  const original = String(raw == null ? '' : raw).trim();
  const out = (phone, status, country, via, p) => ({
    phone, original, changed: phone !== original, status, country: country || null, via: via || null,
    type: p && typeof p.getType === 'function' ? (p.getType() || null) : null
  });
  if (!original) return out('', 'empty');

  const stripped = original.replace(/^(tel|sms|whatsapp):/i, '').trim();
  const sci = expandScientific(stripped);
  if (sci.isSci && !sci.digits) return out(original, 'precision');
  let s = sci.isSci ? sci.digits : stripped.replace(/[^\d+]/g, '');
  let digits = s.replace(/\D/g, '');
  if (digits.length < 6) return out(original, 'needs_country');

  // International call prefixes: "+", "00" (most of the world), and "011"
  // (dialled from North America). "+00…" is the same redundant double prefix.
  let international = s.startsWith('+');
  if (digits.startsWith('00') && digits.length >= 11) { digits = digits.slice(2); international = true; }
  else if (!international && digits.startsWith('011') && digits.length >= 13 && digits[3] !== '1') { digits = digits.slice(3); international = true; }

  if (international) {
    const p = valid(parse('+' + digits));
    if (p) return out(p.number, 'valid', p.country, 'international', p);
    if (isTolerated(digits)) return out('+' + digits, 'tolerated', null, 'international');
    // "+0…": a national number someone put a "+" on. Read it in the default
    // country if there is one ("+0612345678" from a French host).
    if (digits.startsWith('0') && region) {
      const n = valid(parse(digits, region));
      if (n) return out(n.number, 'valid', n.country, 'national', n);
    }
    if (digits.startsWith('0')) return out(original, 'needs_country');
    const loose = parse('+' + digits);
    return out('+' + digits, 'invalid', loose && loose.country, 'international');
  }

  // No international prefix.
  const asIntl = digits.length >= 11 ? valid(parse('+' + digits)) : null;
  if (asIntl) return out(asIntl.number, 'valid', asIntl.country, 'international', asIntl);
  if (region) {
    const n = valid(parse(digits, region));
    if (n) return out(n.number, 'valid', n.country, 'national', n);
  }
  if (digits.length >= 11 && isTolerated(digits)) return out('+' + digits, 'tolerated', null, 'international');
  // Nothing places it in the default country. If the digits make a real
  // number once a "+" goes in front, that is what the old rule dialled and
  // it often delivered (a Seychelles "248…" or Singapore "65…" number typed
  // without the plus: 14 of 33 such stored numbers delivered) — keep it, but
  // as an ASSUMPTION the host is shown.
  const plus = valid(parse('+' + digits));
  if (plus) return out(plus.number, 'assumed', plus.country, 'international', plus);
  // A trunk-0 national number with no country to read it in: left exactly as
  // typed for the host to fix.
  if (digits.startsWith('0')) return out(original, 'needs_country');
  return out('+' + digits, 'needs_country', null, null);
}

/**
 * The country most of these numbers' international forms belong to, if it
 * covers at least half of them and at least 2. A hint for the default
 * country, never applied silently.
 */
function majorityCountry(values) {
  const counts = new Map();
  let total = 0;
  (values || []).forEach((v) => {
    const s = String(v == null ? '' : v).trim();
    if (!s) return;
    let d = s.replace(/[^\d+]/g, '');
    if (d.startsWith('00')) d = '+' + d.slice(2);
    let p = null;
    if (d.startsWith('+')) p = valid(parse(d));
    else if (d.length >= 11) p = valid(parse('+' + d));
    if (!p || !p.country) return;
    total++;
    counts.set(p.country, (counts.get(p.country) || 0) + 1);
  });
  let best = null, n = 0;
  counts.forEach((c, r) => { if (c > n) { best = r; n = c; } });
  return best && n >= 2 && n * 2 >= total ? best : null;
}

/** "+33 6 12 34 56 78" for a valid number, else the input. */
function displayPhone(phone) {
  const p = valid(parse(String(phone || '').trim()));
  return p ? p.formatInternational() : String(phone || '');
}

/** True when libphonenumber knows this ISO country. */
function isSupportedCountry(region) {
  const L = lib();
  return !!(L && region && L.isSupportedCountry(String(region).toUpperCase()));
}

const PhoneResolve = { resolvePhone, majorityCountry, displayPhone, isSupportedCountry, isTolerated, smsDialable, expandScientific, available: () => !!lib() };

if (typeof window !== 'undefined') window.PhoneResolve = PhoneResolve;
if (typeof module !== 'undefined' && module.exports) module.exports = PhoneResolve;
